import { Contact } from '../../../models/misc';

// Release Method Constants
export const ReleaseMethodValues = {
    PRINT_AND_MAIL_ONE: 1,
    DOWNLOAD: 2,
    DIGITAL_FAX: 3,
    PRINTED_LOCALLY: 4,
    PRINT_AND_MAIL: 5,
    ETRANSFER: 6,
} as const;

// Request Purpose IDs
export const DISCLOSURE_EVENT_ID = 43;

/**
 * Checks if a contact has an email address.
 */
export function hasContactEmail(contact?: Contact): boolean {
    return !!contact?.email && contact.email.trim().length > 0;
}

/**
 * Checks if a contact has a fax number.
 */
export function hasContactFax(contact?: Contact): boolean {
    return !!contact?.fax && contact.fax.trim().length > 0;
}

/**
 * Determines the release method based on requestor eTransfer setup, contact information,
 * and request purpose.
 * 
 * Special case: For Disclosure Event (requestPurposeId = 43), always return PRINTED_LOCALLY (4)
 * 
 * Otherwise, priority order:
 * 1. eTransfer (if requestor has it set up) -> ETRANSFER (6)
 * 2. Email (if contact has email) -> DOWNLOAD (2)
 * 3. Fax (if contact has fax but no email) -> DIGITAL_FAX (3)
 * 4. Default -> PRINT_AND_MAIL (5)
 * 
 * @param hasETransferSetup - Whether the requestor has eTransfer set up
 * @param contact - The contact information
 * @param requestPurposeId - The request purpose ID (optional)
 * @returns The release method ID
 */
export function determineReleaseMethod(
    hasETransferSetup: boolean,
    contact?: Contact,
    requestPurposeId?: number
): number {
    // Special case: Disclosure Event should always use "Printed locally" to avoid printing/postage costs
    if (requestPurposeId === DISCLOSURE_EVENT_ID) {
        return ReleaseMethodValues.PRINTED_LOCALLY;
    }

    // Priority 1: eTransfer setup always takes precedence
    if (hasETransferSetup) {
        return ReleaseMethodValues.ETRANSFER;
    }

    // Priority 2: Email (Download)
    if (hasContactEmail(contact)) {
        return ReleaseMethodValues.DOWNLOAD;
    }

    // Priority 3: Fax (Digital Fax)
    if (hasContactFax(contact)) {
        return ReleaseMethodValues.DIGITAL_FAX;
    }

    // Priority 4: Default to Print & Mail
    return ReleaseMethodValues.PRINT_AND_MAIL;
}
